<template>
  <div>
    <b-dropdown
      left
      :data-test="`dropdown-button-${index}`"
      variant="plain"
      toggle-class="text-decoration-none"
      no-caret
    >
      <template v-slot:button-content>
        <i class="fas fa-ellipsis-v" />
      </template>
      <b-dropdown-item
        :data-test="`reporting-link-${index}`"
        :to="{ name: 'ReportingIndex',
               params: {
                 campaignId: item.id,
                 advertiserId: item.advertiserId,
               }
        }"
        :disabled="isReportDisabled"
      >
        View Reporting
      </b-dropdown-item>
      <feature
        name="CAMPAIGN_COPY"
      >
        <div v-if="isMyDashboard">
          <b-dropdown-divider />
          <b-dropdown-item
            @click="onCopyCampaignClick"
            :data-test="`copy-campaign-${index}`"
          >
            Copy Campaign
          </b-dropdown-item>
        </div>
      </feature>
      <div v-if="GET_IS_MANAGED_ACCOUNT && !isReportDisabled">
        <b-dropdown-divider />
        <b-dropdown-item
          :data-test="`campaign-matches-link-${index}`"
          :to="{ name: 'CampaignMatches',
                 params: {
                   campaignId: item.id,
                   advertiserId: item.advertiserId,
                 }
          }"
        >
          Campaign Matches
        </b-dropdown-item>
      </div>
      <b-dropdown-divider />
      <b-dropdown-item
        v-if="!item.group"
        @click="onAddCampaignToGroupClick"
        :data-test="`add-campaign-to-group-${index}`"
      >
        Add Campaign To Group
      </b-dropdown-item>
      <b-dropdown-item
        v-else
        @click="removeCampaignFromGroupClick"
        :data-test="`remove-campaign-from-group-${index}`"
      >
        Remove Campaign Group
      </b-dropdown-item>
      <feature
        name="MANAGED_CAMPAIGNS"
      >
        <div v-if="isInReview && isMyDashboard">
          <b-dropdown-divider />
          <b-dropdown-item
            @click="onSetLiveCampaignClick"
            :data-test="`set-live-campaign-${index}`"
          >
            Set Campaign {{ getCampaignLiveOrPendingState }}
          </b-dropdown-item>
        </div>
      </feature>
      <div v-if="isInReview && !isMyDashboard">
        <b-dropdown-divider />
        <b-dropdown-item
          @click="onSetSelfServiceCampaignLiveClick"
          :data-test="`set-live-campaign-${index}`"
        >
          Set Campaign {{ getCampaignLiveOrPendingState }}
        </b-dropdown-item>
      </div>
      <feature
        name="MANAGED_CAMPAIGNS"
      >
        <div v-if="isInReview">
          <b-dropdown-divider />
          <b-dropdown-item
            @click="setCampaignToDraft"
            :data-test="`set-campaign-draft-${index}`"
          >
            Set Campaign to DRAFT
          </b-dropdown-item>
        </div>
      </feature>
      <div v-if="isMyDashboard">
        <b-dropdown-divider />
        <b-dropdown-item
          @click="showDeleteCampaignConfirmation"
          :disabled="isDeleteDisabled"
          variant="danger"
          :data-test="`delete-campaign-${index}`"
        >
          Delete Campaign
        </b-dropdown-item>
      </div>
      <div v-if="GET_IS_ADMIN">
        <b-dropdown-divider />
        <b-dropdown-item
          @click="showSetDormantLive()"
          :disabled="!isDormant"
          :data-test="`dormant-to-live-campaign-${index}`"
        >
          Set Live Now
        </b-dropdown-item>
      </div>
    </b-dropdown>

    <b-modal
      :id="`CopyCampaignModal-${item.id}`"
      :title="`Copying ${item.name}`"
      data-test="copy-campaign-modal"
      hide-footer
      hide-backdrop
      @shown="shownModal"
    >
      <div class="d-block">
        <b-form-group
          id="fieldset-new-campaign-name"
          label="New campaign name:"
          label-for="new-campaign-name-input"
          label-class="form-label"
          :state="newCampaignNameState"
          :invalid-feedback="invalidFeedback"
        >
          <b-form-input
            id="new-campaign-name-input"
            data-test="new-campaign-input"
            v-model="campaignName"
            size="lg"
            :state="newCampaignNameState"
            trim
          />
          <b-form-checkbox
            id="copyTrackers"
            class="my-4"
            v-model="copyTrackers"
            name="copyTrackers"
            :value="true"
            :unchecked-value="false"
          >
            Copy Trackers
          </b-form-checkbox>
          <b-form-checkbox
            v-if="item.group"
            id="copyGroup"
            class="my-4"
            v-model="copyGroup"
            name="copyGroup"
            :value="true"
            :unchecked-value="false"
          >
            Copy Group
          </b-form-checkbox>
        </b-form-group>
      </div>
      <b-button
        class="mt-3"
        :class="{ 'btn-loading': isLoading }"
        data-test="copy-campaign-button"
        variant="primary"
        block
        @click="copyCampaign"
        :disabled="!newCampaignNameState || isLoading"
      >
        Copy your campaign
      </b-button>
    </b-modal>
    <AddCampaignToGroupModal
      ref="addCampaignToGroupModal"
      :id="item.id"
      :advertiser-id="item.advertiserId"
      @refreshTable="refreshTable"
    />
    <b-modal
      :id="`DeleteCampaignModal-${item.id}`"
      size="md"
      centered
      @ok="onDeleteCampaignClick()"
    >
      <template #modal-title>
        <p
          class="mb-0 font-weight-bold"
        >
          <span class="icon mr-2">
            <i
              class="fas fa-exclamation-triangle warning"
            />
          </span>
          Delete Campaign
        </p>
      </template>
      <p class="mb-0">
        Are you sure you want to delete <strong>"{{ item.name }}"</strong>?
      </p>
      <br>
      <p class="mb-0">
        This action cannot be undone
      </p>
      <template
        #modal-footer="{ cancel, ok }"
      >
        <b-button
          size="md"
          variant="tertiary"
          @click="cancel()"
        >
          Cancel
        </b-button>
        <b-button
          size="md"
          variant="danger"
          @click="ok()"
        >
          Delete
        </b-button>
      </template>
    </b-modal>
    <dormant-to-live-modal
      ref="dormantToLiveModal"
      :campaign-id="item.id"
      :advertiser-id="item.advertiserId"
      @updateState="updateState"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import * as types from '@/modules/campaigns/store/types';
import * as accountTypes from '@/modules/account/store/types';
import * as authenticationTypes from '@/modules/authentication/store/types';

import helper from '@/core/helper';

import AddCampaignToGroupModal from '@/modules/campaigns/components/AddCampaignToGroupModal.vue';
import DormantToLiveModal from '@/modules/campaigns/components/CampaignInfo/DormantCampaignToLiveConfirmationModal';

export default {
  name: 'CampaignActions',
  components: {
    DormantToLiveModal,
    AddCampaignToGroupModal,
  },
  props: {
    index: {
      type: Number,
      required: true,
    },
    item: {
      type: Object,
      required: true,
    },
    showEnded: {
      type: Boolean,
      default: false,
    },
    isMyDashboard: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      clicked: false,
      isLoading: false,
      campaignName: '',
      checkNameExists: false,
      copyTrackers: true,
      copyGroup: true,
      minAvailableDateForReports: process.env.VUE_APP_MIN_AVAILABLE_REPORTING_DATE,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaigns',
    ]),
    ...mapGetters('account', [
      accountTypes.GET_IS_MANAGED_ACCOUNT,
    ]),
    ...mapGetters('authentication', [
      authenticationTypes.GET_IS_ADMIN,
    ]),
    newCampaignNameState() {
      if (this.checkNameExists) {
        return false;
      }
      if (this.campaignName.length > 4) {
        return true;
      }
      return false;
    },
    invalidFeedback() {
      if (this.checkNameExists) {
        return 'This name is already being used for another campaign';
      }
      if (this.campaignName.length > 0) {
        return 'Enter at least 4 characters';
      }
      return 'Please enter something';
    },
    isReportDisabled() {
      return this.isInDraft || this.isInReview || this.item.endDate < this.minAvailableDateForReports;
    },
    isDeleteDisabled() {
      return !this.item.state || !this.isInDraft;
    },
    isInDraft() {
      return this.item.state === 'DRAFT';
    },
    isInReview() {
      return this.item.state === 'REVIEW';
    },
    isDormant() {
      return this.item.state === 'DORMANT';
    },
    getCampaignLiveOrPendingState() {
      const today = new Date();
      const startDate = new Date(this.item.startDate * 1000);
      return startDate > today ? 'Pending' : 'Live';
    },
  },
  methods: {
    ...mapActions('campaigns', [
      types.COPY_CAMPAIGN,
      types.SET_CAMPAIGN_LIVE,
      types.SET_CAMPAIGN_STATE,
      types.GET_CAMPAIGN_BY_NAME,
      types.PATCH_CAMPAIGN,
    ]),
    showDeleteCampaignConfirmation() {
      this.$root.$emit('bv::show::modal', `DeleteCampaignModal-${this.item.id}`);
    },
    showSetDormantLive() {
      this.$root.$emit('bv::show::modal', `dormantToLiveModal-${this.item.id}`);
    },
    onDeleteCampaignClick() {
      this.$emit('removeCampaign', this.item);
    },
    onCopyCampaignClick() {
      this.$root.$emit('bv::show::modal', `CopyCampaignModal-${this.item.id}`);
    },
    onAddCampaignToGroupClick() {
      this.$refs.addCampaignToGroupModal.showModal();
    },
    removeCampaignFromGroupClick() {
      this.PATCH_CAMPAIGN({ group: '', advertiserId: this.item.advertiserId, id: this.item.id })
        .then(() => {
          this.$emit('refreshTable');
        });
    },
    onSetLiveCampaignClick() {
      this.$emit('startOrPauseCampaign', this.item, 'LIVE');
    },
    onSetSelfServiceCampaignLiveClick() {
      this.SET_CAMPAIGN_LIVE(this.item.id);
      this.$emit('refreshTable');
    },
    setCampaignToDraft() {
      this.$emit('setCampaignToDraft', this.item);
    },
    checkCampaignName: helper.debounce(function checkCampaignName() {
      if (this.campaignName !== '') {
        this.GET_CAMPAIGN_BY_NAME(this.campaignName)
          .then(({ status }) => {
            if (status !== 200) {
              this.checkNameExists = false;
              return;
            }
            this.checkNameExists = true;
          });
      }
      this.checkNameExists = false;
    }, 1000),
    copyCampaign() {
      if (this.clicked) return;
      this.clicked = true;
      this.isLoading = true;
      const payload = {
        campaignId: this.item.id,
        advertiserId: this.item.advertiserId,
        toCampaignName: this.campaignName,
        copyTrackers: this.copyTrackers,
        copyGroup: this.copyGroup,
      };
      this.COPY_CAMPAIGN(payload)
        .then((campaign) => {
          this.clicked = false;
          this.isLoading = false;
          this.$root.$emit('bv::hide::modal', `CopyCampaignModal-${this.item.id}`);
          this.$router.push(`/new-campaign/aid/${campaign.advertiserId}/cid/${campaign.id}`);
        }).catch(() => {
          this.clicked = false;
          this.isLoading = false;
        });
    },
    shownModal() {
      this.campaignName = `(Copy) ${this.item.name}`;
    },
    refreshTable() {
      this.$emit('refreshTable');
    },
    updateState() {
      this.$emit('setDormantToLive', this.item, 'LIVE');
    },
  },
  watch: {
    campaignName() {
      this.checkCampaignName();
    },
  },
};
</script>
